import { validations } from "@/utils/validationUtils";
import { App as AntApp, Checkbox, Col, Form, Modal, Row, Upload } from "antd";
import { useForm } from "antd/es/form/Form";
import { RcFile } from "antd/lib/upload";
import { UploadFile } from "antd/lib/upload/interface";
import { useTranslation } from "react-i18next";
import { HttpContentType } from "../../../../common/api/http";
import { useImportAgents } from "../../../../common/api/queries";
import ActionButton from "../../../../common/components/buttons/ActionButton";
import LabelWithTooltip from "../../../../common/components/form/labels/LabelWithTooltip";
import { FileUploadAccept, ModalSizes, rowGutter } from "../../../../common/constants";

type Props = {
  isOpen: boolean;
  onFormCancel: () => void;
};

export const AgentsImportModalForm = ({ isOpen, onFormCancel }: Props) => {
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const [form] = useForm<{ dryRun: boolean; allowUpdate: boolean; file: RcFile }>();

  const importAgents = useImportAgents();

  const handleBeforeUpload = (file: RcFile) => {
    if (!HttpContentType.CSV.includes(file.type)) {
      notification.error({ title: t("common.error"), description: t("helpers.onlyCsvSupported") });
      return Upload.LIST_IGNORE;
    }

    return false;
  };

  const handleFormSubmit = () => {
    form.validateFields().then((values) => {
      const formData = new FormData();

      formData.append("file", values.file);
      formData.append("dryRun", values.dryRun.toString());
      formData.append("allowUpdate", values.allowUpdate.toString());

      importAgents.mutate(formData, {
        onSuccess: onFormCancel,
      });
    });
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      title={t("agent.titles.import")}
      open={isOpen}
      okText={t("common.submit")}
      cancelText={t("common.cancel")}
      confirmLoading={importAgents.isPending}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      afterClose={() => form.resetFields()}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="agentsImportForm">
        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item
              name="dryRun"
              valuePropName="checked"
              className="margin-top-small"
              rules={[validations.none]}
              initialValue={true}
            >
              <Checkbox>
                <LabelWithTooltip
                  label={t("agent.helpers.import.dryRun")}
                  tooltip={t("agent.helpers.import.dryRunDesc")}
                />
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item name="allowUpdate" valuePropName="checked" rules={[validations.none]} initialValue={false}>
              <Checkbox>
                <LabelWithTooltip
                  label={t("agent.helpers.import.allowUpdate")}
                  tooltip={t("agent.helpers.import.allowUpdateDesc")}
                />
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item
              name="file"
              getValueFromEvent={({ file }: { file: UploadFile | RcFile }) => {
                return "status" in file && file.status === "removed" ? undefined : file;
              }}
              rules={[validations.notNull]}
              valuePropName="file"
            >
              <Upload maxCount={1} accept={FileUploadAccept.CSV} beforeUpload={handleBeforeUpload}>
                <ActionButton icon="plus">
                  <span className="form-item-required-after">{t("common.chooseFile")}</span>
                </ActionButton>
              </Upload>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
